import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCw, ArrowRight, Clock } from "lucide-react";
import { formatTime } from "@/lib/format";

export default function StaffOverviewPage() {
    const navigate = useNavigate();
    const [reservations, setReservations] = useState([]);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async () => {
        try {
            setRefreshing(true);
            const [r, o] = await Promise.all([
                api.get("/staff/today"),
                api.get("/staff/orders/active"),
            ]);
            setReservations(r.data || []);
            setOrders(o.data || []);
        } catch (error) {
            console.warn("[staff-overview] load failed", error?.message);
        } finally {
            setRefreshing(false);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 6000);
        return () => clearInterval(id);
    }, [load]);

    const upcoming = reservations.filter(r => r.status === "booked").length;
    const checked = reservations.filter(r => r.status === "checked_in").length;
    const seated = reservations.filter(r => r.status === "seated").length;
    const itemsToFire = orders.reduce((s, o) => s + o.items.filter(i => i.status === "received" || i.status === "preparing").length, 0);

    if (loading) return <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8"><Skeleton className="h-72 w-full" /></div>;

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl">Today at a glance</h1>
                    <p className="text-sm text-muted-foreground">Live status of reservations and kitchen orders.</p>
                </div>
                <Button variant="outline" onClick={load} data-testid="staff-refresh-button"><RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh</Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Stat label="Upcoming" value={upcoming} sub="booked" />
                <Stat label="Checked in" value={checked} sub="awaiting seat" />
                <Stat label="Seated" value={seated} sub="currently dining" />
                <Stat label="Items in queue" value={itemsToFire} sub="kitchen workload" />
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="font-display text-xl">Reservations</h2>
                            <Button size="sm" variant="secondary" onClick={() => navigate("/staff/host")} data-testid="go-host-button">
                                Host view <ArrowRight className="h-3.5 w-3.5 ml-1" />
                            </Button>
                        </div>
                        <div className="space-y-2">
                            {reservations.slice(0, 6).map(r => (
                                <div key={r.id} className="flex items-center justify-between p-3 rounded-lg border border-border" data-testid="overview-reservation-row">
                                    <div className="flex items-center gap-3">
                                        <Clock className="h-4 w-4 text-muted-foreground" />
                                        <div>
                                            <div className="text-sm font-medium">{r.customer?.name || "Guest"} · {r.party_size}p</div>
                                            <div className="text-xs text-muted-foreground">{formatTime(r.reservation_time)} {r.table?.label ? `· Table ${r.table.label}` : ""}</div>
                                        </div>
                                    </div>
                                    <Badge variant="outline" className="capitalize">{r.status.replace("_", " ")}</Badge>
                                </div>
                            ))}
                            {reservations.length === 0 && <div className="text-sm text-muted-foreground">No reservations today.</div>}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="font-display text-xl">Active kitchen orders</h2>
                            <span className="text-xs text-muted-foreground">Read-only · Kitchen team manages prep</span>
                        </div>
                        <div className="space-y-2">
                            {orders.slice(0, 6).map(o => (
                                <div key={o.id} className="p-3 rounded-lg border border-border" data-testid="overview-order-row">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm font-medium">Table {o.table?.label || "—"} · {o.customer?.name}</div>
                                        <div className="text-xs text-muted-foreground">{o.items.length} items</div>
                                    </div>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {o.items.slice(0, 3).map(it => (
                                            <Badge key={it.id} variant="outline" className="capitalize text-xs">{it.name} — {it.status}</Badge>
                                        ))}
                                        {o.items.length > 3 && <span className="text-xs text-muted-foreground">+{o.items.length - 3} more</span>}
                                    </div>
                                </div>
                            ))}
                            {orders.length === 0 && <div className="text-sm text-muted-foreground">No active orders.</div>}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

function Stat({ label, value, sub }) {
    return (
        <Card><CardContent className="p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
            <div className="font-display text-4xl mt-1">{value}</div>
            <div className="text-xs text-muted-foreground">{sub}</div>
        </CardContent></Card>
    );
}
